import React, { useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useCustomizationStore } from './store/customizationStore';
import { MainPage } from './components/MainPage';
import { MaterialSelection } from './components/MaterialSelection';
import { PurchasePath } from './components/PurchasePath';
import { CustomizationType } from './components/CustomizationType';
import { Customize } from './components/Customize';
import { InquiryForm } from './components/InquiryForm';
import { Confirmation } from './components/Confirmation';
import { ProgressBar } from './components/ProgressBar';
import FloatingInquiryButton from './components/FloatingInquiryButton';
import { CinematicIntro } from './components/CinematicIntro';
import { DesignCinematic } from './components/DesignCinematic';
import { MaterialComparison } from './components/MaterialComparison';
import { AppErrorBoundary } from './components/ErrorBoundary';
import { LoadingSpinner } from './components/LoadingSpinner';
import { MarketingHeader } from './components/MarketingHeader';

const App: React.FC = () => {
  const { currentStep } = useCustomizationStore();

  // Add ready class to root element once app is mounted
  useEffect(() => {
    const root = document.getElementById('root');
    if (root) {
      root.classList.add('ready');
    }

    // Handle offline capability
    window.addEventListener('app-ready-offline', () => {
      // Show offline-ready notification
      const notification = document.createElement('div');
      notification.className = 'fixed bottom-4 left-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg';
      notification.textContent = 'App ready to work offline';
      document.body.appendChild(notification);
      setTimeout(() => notification.remove(), 3000);
    });

    // Handle app updates
    window.addEventListener('app-update-available', () => {
      // Show update notification
      const notification = document.createElement('div');
      notification.className = 'fixed bottom-4 left-4 bg-blue-500 text-white px-6 py-3 rounded-lg shadow-lg';
      notification.textContent = 'New version available. Refresh to update.';
      document.body.appendChild(notification);
      setTimeout(() => notification.remove(), 5000);
    });

    return () => {
      window.removeEventListener('app-ready-offline', () => {});
      window.removeEventListener('app-update-available', () => {});
    };
  }, []);

  // Add scroll to main content effect when step changes
  useEffect(() => {
    const mainContent = document.querySelector('.configurator-container');
    if (mainContent) {
      const progressBar = document.querySelector('.progress-bar');
      const offset = progressBar ? progressBar.getBoundingClientRect().height : 0;
      const targetPosition = mainContent.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: targetPosition, behavior: 'smooth' });
    }
  }, [currentStep]);

  // Add dark theme class for specific pages
  useEffect(() => {
    const container = document.querySelector('.configurator-container');
    if (container) {
      if (currentStep === 'material-selection' || currentStep === 'confirmation') {
        container.classList.add('dark-theme');
      } else {
        container.classList.remove('dark-theme');
      }
    }
  }, [currentStep]);

  return (
    <AppErrorBoundary>
      <React.Suspense fallback={<LoadingSpinner />}>
        <div className="configurator-container">
          <ProgressBar />
          <AnimatePresence mode="wait">
            {currentStep === 'cinematic' && <CinematicIntro />}
            {currentStep === 'material-selection' && <MaterialSelection />}
            {currentStep === 'purchase-path' && <PurchasePath />}
            {currentStep === 'customization-type' && <CustomizationType />}
            {currentStep === 'customize' && <Customize />}
            {currentStep === 'inquiry' && <InquiryForm />}
            {currentStep === 'confirmation' && <Confirmation />}
            {currentStep === 'material-comparison' && <MaterialComparison />}
            {!currentStep && <MainPage />}
          </AnimatePresence>
          <FloatingInquiryButton />
          <MarketingHeader />
        </div>
      </React.Suspense>
    </AppErrorBoundary>
  );
};

export default App;